import React from 'react';

export type CardVariant = 'standard' | 'interactive' | 'dashed';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: React.ElementType;
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ as: Component = 'div', className = '', children, ...props }, ref) => (
    <Component
      ref={ref}
      className={`flex items-center justify-between gap-3 border-b border-base-300 pb-3 mb-3 ${className}`}
      {...props}
    >
      {children}
    </Component>
  )
);
CardHeader.displayName = 'CardHeader';

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: React.ElementType;
}

export const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  ({ as: Component = 'div', className = '', children, ...props }, ref) => (
    <Component ref={ref} className={`flex-1 min-w-0 ${className}`} {...props}>
      {children}
    </Component>
  )
);
CardBody.displayName = 'CardBody';

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: React.ElementType;
}

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ as: Component = 'div', className = '', children, ...props }, ref) => (
    <Component
      ref={ref}
      className={`flex items-center justify-between gap-3 border-t border-base-300 pt-3 mt-3 ${className}`}
      {...props}
    >
      {children}
    </Component>
  )
);
CardFooter.displayName = 'CardFooter';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: React.ElementType;
  variant?: CardVariant;
  padding?: CardPadding;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

interface CardComponent
  extends React.ForwardRefExoticComponent<CardProps & React.RefAttributes<HTMLDivElement>> {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      as: Component = 'div',
      variant = 'standard',
      padding = 'md',
      header,
      footer,
      children,
      className = '',
      onClick,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const isInteractive = variant === 'interactive' || Boolean(onClick);

    const paddingClasses: Record<CardPadding, string> = {
      none: 'p-0',
      sm: 'p-4',
      md: 'p-5 lg:p-6',
      lg: 'p-6 lg:p-8'
    };

    const variantClasses: Record<CardVariant, string> = {
      standard: 'bg-base-200 border border-base-300 rounded-2xl',
      interactive:
        'bg-base-200 border border-base-300 rounded-2xl cursor-pointer transition hover:border-primary/40 hover:shadow-lg active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 ring-primary ring-offset-2 ring-offset-base-100',
      dashed: 'bg-transparent border border-dashed border-base-300 rounded-2xl'
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e);
      if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }
    };

    return (
      <Component
        ref={ref}
        role={isInteractive && Component === 'div' ? 'button' : undefined}
        tabIndex={isInteractive && Component === 'div' ? 0 : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={`${variantClasses[variant]} ${paddingClasses[padding]} ${className}`}
        {...props}
      >
        {header && <CardHeader>{header}</CardHeader>}
        {children}
        {footer && <CardFooter>{footer}</CardFooter>}
      </Component>
    );
  }
) as CardComponent;

Card.displayName = 'Card';
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
